"use client"
/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useContext } from "react"
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react"
import axios from "axios"
import { AuthContext } from "../../../Auth/context/AuthContext"

interface SchemaStepProps {
  excelFile: File
  sampleHeaders: string[]
  sampleRows: string[][]
  onSuccess: (schema: string[], examples: Record<string, string>[]) => void
  onBack: () => void
  onError: (error: string) => void
}

export default function SchemaStep({
  excelFile,
  sampleHeaders,
  sampleRows,
  onSuccess,
  onBack,
  onError,
}: SchemaStepProps) {
  const { auth } = useContext(AuthContext)
  const [loading, setLoading] = useState(true)
  const [schema, setSchema] = useState<string[]>([])
  const [customFields, setCustomFields] = useState<string[]>([])
  const [exampleRows, setExampleRows] = useState<Record<string, string>[]>([])

  // Fixed fields that must be present
  const fixedFields = [
    "Name",
    "Email",
    "Number",
    "Contact Type",
    "Sub-Type",
    "Company Name",
    "Office Name",
    "Address",
    "City",
    "StateProvince",
    "Country",
  ]
  const fixedSet = new Set(fixedFields)

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    const generate = async () => {
      if (!excelFile || !auth?.token) return
      setLoading(true)
      onError("")
      try {
        const form = new FormData()
        form.append("excelFile", excelFile)
        const resp = await axios.post(
          "/api/myteconstruction/contact-import/schema",
          form,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        )
        const full = resp.data.finalSchema as string[]
        // separate extras = anything not in core list
        const extras = full.filter(f => !fixedSet.has(f))

        setSchema(fixedFields)     // keep state for core only
        setCustomFields(extras)    // pre-populate custom list, editable
        setExampleRows(resp.data.exampleRows)
      } catch (err) {
        console.error("Schema error:", err)
        onError("Failed to generate schema. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    generate()
  }, [excelFile, sampleHeaders, sampleRows, auth?.token])

  const addCustomField = () => {
    setCustomFields(prev => [...prev, ""])
  }

  const updateCustomField = (index: number, value: string) => {
    const updated = [...customFields]
    updated[index] = value
    // prevent duplicate keys (case-insensitive)
    if (updated
          .map(f => f.toLowerCase().trim())
          .filter((v,i,arr) => arr.indexOf(v)!==i).length === 0) {
      setCustomFields(updated)
    }
  }

  const removeCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index))
  }

  const handleContinue = () => {
    // Combine fixed fields and custom fields
    const finalSchema = [...fixedFields, ...customFields.filter((f) => f.trim())]

    // Add custom fields to example rows
    const updatedExamples = exampleRows.map((row) => {
      const updated = { ...row }
      customFields.forEach((field) => {
        if (field.trim()) {
          updated[field] = ""
        }
      })
      return updated
    })

    onSuccess(finalSchema, updatedExamples)
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-white mb-4">Map Fields</h3>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Fixed Fields */}
          <div>
            <h4 className="font-medium text-gray-300 mb-3">Required Fields</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fixedFields.map((field) => (
                <div key={field} className="bg-gray-700 p-2 rounded-md flex items-center">
                  <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded text-xs mr-2">Required</span>
                  <span>{field}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Fields */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-300">Custom Fields</h4>
              <button
                onClick={addCustomField}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add Field</span>
              </button>
            </div>

            {customFields.length > 0 ? (
              <div className="space-y-2">
                {customFields.map((field, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={field}
                      onChange={(e) => updateCustomField(index, e.target.value)}
                      placeholder="Custom field name"
                      className="flex-1 bg-gray-700 text-white rounded-md p-2 border border-gray-600"
                    />
                    <button
                      onClick={() => removeCustomField(index)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                      title="Remove field"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No custom fields. Click "Add Field" to create one.</p>
            )}
          </div>

          {/* Preview */}
          <div>
            <h4 className="font-medium text-gray-300 mb-3">Preview</h4>
            <div className="bg-gray-700 p-3 rounded-md overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    {schema.map((field) => (
                      <th key={field} className="p-2 text-left font-medium text-gray-300 border-b border-gray-600">
                        {field}
                      </th>
                    ))}
                    {customFields.map(
                      (field, index) =>
                        field && (
                          <th
                            key={`custom-${index}`}
                            className="p-2 text-left font-medium text-gray-300 border-b border-gray-600"
                          >
                            {field}
                          </th>
                        ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {exampleRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {schema.map((field) => (
                        <td key={field} className="p-2 border-b border-gray-600">
                          {row[field] || ""}
                        </td>
                      ))}
                      {customFields.map(
                        (field, index) =>
                          field && (
                            <td key={`custom-${index}`} className="p-2 border-b border-gray-600">
                              {""}
                            </td>
                          ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <button
              onClick={handleContinue}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
